import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Zap, Eye, Clock, Heart, ArrowLeft, Link2, Twitter, MessageCircle, BookOpen } from "lucide-react";
import api from "@/lib/api";
import { catBySlug } from "@/data/categories";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FactCard } from "@/components/FactCard";
import { FavouriteButton } from "@/components/FavouriteButton";

export default function FactDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [fact, setFact] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setFact(null);
    setNotFound(false);
    api
      .get(`/facts/${slug}`)
      .then((r) => {
        setFact(r.data);
        setLikes(r.data.likes || 0);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const doLike = async () => {
    if (!fact || liked) return;
    setLiked(true);
    setLikes((l) => l + 1);
    try {
      const { data } = await api.post(`/facts/${fact.id}/like`);
      setLikes(data.likes);
      toast.success("Mind blown counted 🤯", { className: "font-mono" });
    } catch {
      setLiked(false);
      setLikes((l) => l - 1);
    }
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard", { className: "font-mono" });
  };
  const shareText = fact ? encodeURIComponent(`${fact.title} — via OddlyTrue`) : "";

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <p className="font-display text-4xl font-bold text-ink">That fact wandered off.</p>
          <p className="mt-3 text-ink-muted font-mono">It may have been unpublished, or never existed.</p>
          <Link to="/" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink text-white font-mono text-sm hover:bg-gold hover:text-ink transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to all facts
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!fact) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 animate-pulse space-y-6">
          <div className="h-8 w-40 bg-slate-100 rounded-full" />
          <div className="h-16 w-full bg-slate-100 rounded-2xl" />
          <div className="h-72 w-full bg-slate-100 rounded-2xl" />
        </div>
        <Footer />
      </div>
    );
  }

  const cat = catBySlug(fact.category);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
        <button onClick={() => navigate(-1)} className="mb-8 inline-flex items-center gap-2 font-mono text-sm text-ink-muted hover:text-gold transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <Link to="/" className={`px-3 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${cat.chip}`}>
            {cat.name}
          </Link>
          <span className="flex items-center gap-1 text-xs font-mono text-gold"><Zap className="w-3.5 h-3.5" /> {fact.weirdness}/100 weird</span>
          <span className="flex items-center gap-1 text-xs font-mono text-ink-muted"><Clock className="w-3.5 h-3.5" /> {fact.read_time} min</span>
          <span className="flex items-center gap-1 text-xs font-mono text-ink-muted"><Eye className="w-3.5 h-3.5" /> {fact.views?.toLocaleString?.()}</span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="fact-detail-title"
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.05] tracking-tight text-ink"
        >
          {fact.title}
        </motion.h1>

        <p className="mt-5 text-lg sm:text-xl text-ink leading-relaxed border-l-2 border-gold pl-5">{fact.summary}</p>

        <div className="mt-8 rounded-3xl overflow-hidden border border-slate-200">
          <img src={fact.image_url} alt={fact.title} className="w-full aspect-[16/9] object-cover" />
        </div>

        {/* Weirdness meter */}
        <div className="mt-8 p-5 rounded-2xl bg-slate-50 border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs uppercase tracking-widest text-ink-muted">Weirdness Meter</span>
            <span className="font-mono text-sm text-gold">{fact.weirdness}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${fact.weirdness}%` }} transition={{ duration: 0.8 }} className="h-full bg-gradient-to-r from-gold to-ink" />
          </div>
        </div>

        {/* Body */}
        <div className="mt-8 space-y-5">
          {String(fact.body).split("\n\n").map((p, i) => (
            <p key={i} className="text-base sm:text-lg leading-relaxed text-ink">{p}</p>
          ))}
        </div>

        {/* Tags + source */}
        <div className="mt-8 flex flex-wrap gap-2">
          {(fact.tags || []).map((t) => (
            <Link key={t} to="/" className="px-3 py-1 rounded-full text-xs font-mono bg-slate-50 border border-slate-200 text-ink-muted hover:border-gold hover:text-gold transition-colors">
              #{t}
            </Link>
          ))}
        </div>
        {fact.source && (
          <p className="mt-5 flex items-center gap-2 font-mono text-xs text-ink-muted">
            <BookOpen className="w-3.5 h-3.5" /> Source: {fact.source}
          </p>
        )}

        {/* Actions */}
        <div className="mt-10 flex flex-wrap items-center gap-3 pt-6 border-t border-slate-200">
          <button
            data-testid="mind-blown-button"
            onClick={doLike}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-mono text-sm border transition-all ${liked ? "bg-ink text-white border-gold" : "bg-white text-ink border-slate-300 hover:border-gold hover:text-gold"}`}
          >
            <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} /> Mind Blown · {likes}
          </button>
          <FavouriteButton factId={fact.id} variant="pill" />
          <div className="flex items-center gap-2 ml-auto">
            <a data-testid="share-button-twitter" href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" className="p-2.5 rounded-full bg-white border border-slate-300 text-ink-muted hover:text-gold hover:border-gold transition-colors">
              <Twitter className="w-4 h-4" />
            </a>
            <a data-testid="share-button-whatsapp" href={`https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" className="p-2.5 rounded-full bg-white border border-slate-300 text-ink-muted hover:text-gold hover:border-gold transition-colors">
              <MessageCircle className="w-4 h-4" />
            </a>
            <button data-testid="share-button-copy" onClick={copyLink} className="p-2.5 rounded-full bg-white border border-slate-300 text-ink-muted hover:text-gold hover:border-gold transition-colors">
              <Link2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </article>

      {/* Related */}
      {fact.related?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-6 text-ink">Keep falling down the hole</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {fact.related.map((f, i) => (
              <FactCard key={f.id} fact={f} index={i} />
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
